import { useMemo, useState } from "react";
import { Box, Button, InputAdornment, Menu, TextField, Typography } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from "@mui/icons-material/Search";

import HomeMenuItemCollapsable from "./home-menu-item-collapsable";
import Translate from "component/global/translation/translation-component";
import { useMenuDefinition } from "shared/hooks/useMenuItems";
import HomeMenuItem from "./home-menu-item";
import { useTranslation } from "react-i18next";

const OPEN_SUBMENUS_STORAGE_KEY = "home_menu.open_submenus";
const LAST_OPEN_SUBMENU_STORAGE_KEY = "home_menu.last_open_submenu";

const MenuDefinitionComponent = () => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [openSubmenuIds, setOpenSubmenuIds] = useState<Set<string>>(() => {
        if (typeof window === "undefined") {
            return new Set();
        }

        const storedOpenSubmenus = window.localStorage.getItem(OPEN_SUBMENUS_STORAGE_KEY);
        if (storedOpenSubmenus) {
            try {
                const parsedOpenSubmenus = JSON.parse(storedOpenSubmenus);

                if (Array.isArray(parsedOpenSubmenus)) {
                    return new Set(parsedOpenSubmenus.map(String));
                }
            } catch {
                window.localStorage.removeItem(OPEN_SUBMENUS_STORAGE_KEY);
            }
        }

        const lastOpenSubmenuId = window.localStorage.getItem(LAST_OPEN_SUBMENU_STORAGE_KEY);
        return lastOpenSubmenuId ? new Set([lastOpenSubmenuId]) : new Set();
    });
    const open = Boolean(anchorEl);

    const { menuTree } = useMenuDefinition();
    const { t } = useTranslation();

    const collapsableMenuIds = useMemo(
        () => new Set((menuTree ?? []).filter((node) => node.children.length > 0).map((node) => String(node.record.id))),
        [menuTree]
    );

    const getSearchableText = (record: { [key: string]: any }) =>
        [
            record.label ? t(record.label) : "",
            record.label,
            record.name,
            record.title,
            record.link_to,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

    const filteredMenuTree = useMemo(() => {
        const normalizedSearchQuery = searchQuery.trim().toLowerCase();

        if (!normalizedSearchQuery) {
            return menuTree;
        }

        return menuTree
            .map((node) => {
                const parentMatches = getSearchableText(node.record).includes(normalizedSearchQuery);
                const matchingChildren = node.children.filter((child) =>
                    getSearchableText(child.record).includes(normalizedSearchQuery)
                );

                if (!parentMatches && matchingChildren.length === 0) {
                    return null;
                }

                return {
                    ...node,
                    children: parentMatches ? node.children : matchingChildren,
                };
            })
            .filter((node): node is NonNullable<typeof node> => Boolean(node));
    }, [menuTree, searchQuery, t]);

    const handleClose = () => {
        setAnchorEl(null);
        setSearchQuery("");
    };

    const persistOpenSubmenus = (nextOpenSubmenuIds: Set<string>) => {
        const validOpenSubmenuIds = [...nextOpenSubmenuIds].filter((submenuId) => collapsableMenuIds.has(submenuId));

        if (typeof window === "undefined") {
            return;
        }

        if (validOpenSubmenuIds.length === 0) {
            window.localStorage.removeItem(OPEN_SUBMENUS_STORAGE_KEY);
            window.localStorage.removeItem(LAST_OPEN_SUBMENU_STORAGE_KEY);
            return;
        }

        window.localStorage.setItem(OPEN_SUBMENUS_STORAGE_KEY, JSON.stringify(validOpenSubmenuIds));
        window.localStorage.setItem(LAST_OPEN_SUBMENU_STORAGE_KEY, validOpenSubmenuIds[validOpenSubmenuIds.length - 1]);
    };

    const handleSubmenuToggle = (submenuId: string, isOpen: boolean) => {
        const nextOpenSubmenuIds = new Set(openSubmenuIds);

        if (isOpen && collapsableMenuIds.has(submenuId)) {
            nextOpenSubmenuIds.add(submenuId);
        } else {
            nextOpenSubmenuIds.delete(submenuId);
        }

        setOpenSubmenuIds(nextOpenSubmenuIds);
        persistOpenSubmenus(nextOpenSubmenuIds);
    };

    if (!menuTree) return null;

    return (
        <>
            <Button
                variant="text"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ color: "primary.main" }} />}
                sx={{
                    borderRadius: 999,
                    color: "primary.main",
                    px: 1.5,
                    textTransform: "none",
                    "&:hover": {
                        backgroundColor: "action.hover",
                    },
                }}
            >
                <Translate text="Menu" variant="button" sx={{ color: "primary.main", fontWeight: 700 }} />
            </Button>

            <Menu
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableAutoFocusItem
                PaperProps={{
                    elevation: 8,
                    sx: {
                        mt: 1,
                        width: 340,
                        maxWidth: "calc(100vw - 32px)",
                        borderRadius: (theme) => `${Math.min(Number(theme.shape.borderRadius) || 0, 10)}px`,
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        boxShadow: "0 18px 50px rgba(15, 23, 42, 0.16)",
                        overflow: "hidden",
                    },
                }}
                MenuListProps={{
                    sx: {
                        p: 0,
                    },
                }}
            >
                <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
                    <TextField
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        placeholder={String(t("Search") || "Search")}
                        size="small"
                        fullWidth
                        autoComplete="off"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 999,
                                backgroundColor: "action.selected",
                                "& fieldset": {
                                    borderColor: "transparent",
                                },
                                "&:hover fieldset": {
                                    borderColor: "primary.light",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "primary.main",
                                    borderWidth: 1,
                                },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ maxHeight: 420, overflowY: "auto", py: 1, px: 0.75 }}>
                    {filteredMenuTree.length > 0 ? (
                        filteredMenuTree.map((node) =>
                            node.children.length > 0 ? (
                                <HomeMenuItemCollapsable
                                    key={node.record.id}
                                    currentMenuRecord={node.record}
                                    menuRecordList={node.children.map((child) => child.record)}
                                    onClose={handleClose}
                                    isOpen={searchQuery.trim() ? true : openSubmenuIds.has(String(node.record.id))}
                                    onToggle={(isOpen) =>
                                        handleSubmenuToggle(String(node.record.id), isOpen)
                                    }
                                />
                            ) : (
                                <HomeMenuItem
                                    key={node.record.id}
                                    currentMenuRecord={node.record}
                                    handleClose={handleClose}
                                />
                            )
                        )
                    ) : (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, py: 3, textAlign: "center" }}
                        >
                            {t("No results found")}
                        </Typography>
                    )}
                </Box>
            </Menu>
        </>
    );
};


export default MenuDefinitionComponent;
